import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Skeleton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetTimetableWeekQuery } from "../../Api/attoDeskApi";
import { Loader, PageOverlayLoader } from "../../Components/Loader";
import SummaryCards, { TodaySummary } from "./SummaryCards";
import { deriveTodaySummary } from "./deriveSummary";
import FilterBar, { TodayFilters, EMPTY_FILTERS } from "./FilterBar";
import DayList from "./DayList";
import ScheduleTimeline from "./ScheduleTimeline";
import ClassDrawer, { ClassDrawerData } from "./ClassDrawer";
import Legend from "./Legend";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";

dayjs.extend(isoWeek);

/**
 * "Today" page.
 *
 * Owns the shared state that drives everything on-screen:
 *   - `selectedDate`  → drives the summary card values and the highlighted
 *     day tab. Also becomes the `reference_date` so the week bar snaps to
 *     the ISO week that contains the selection.
 *   - `filters`       → split into server filters (sent to the endpoint,
 *     narrow the payload) and client filters (applied over the returned
 *     occurrences without a round trip). See FilterBar.tsx for the split.
 *
 * The grid, drawer, and star indicator arrive in Phases 4–7. The
 * `filteredOccurrences` memo below is already the source of truth those
 * later phases will render from — so wiring them in is a drop-in.
 */
type OccurrenceRow = {
  id: number;
  class_id: number;
  class_name: string | null;
  scheduled_date: string;
  start_time: string | null;
  end_time: string | null;
  staff_id: number | null;
  subject: string | null;
  grade: string | null;
  teaching_mode: "Online" | "Offline" | "Hybrid" | null;
  location_id: number | null;
  location_name: string | null;
  status: string | null;
  is_cancelled: boolean;
  is_makeup: boolean;
  capacity: number;
  assigned_count: number;
  available_spots: number;
  is_full: boolean;
  students: Array<{
    id: number;
    name: string;
    initials: string;
    image?: string | null;
    attendance_status?: string | null;
    has_outstanding_invoice: boolean;
    outstanding_invoice?: {
      id: number;
      invoice_number: string;
      status: string;
      balance_due: number;
      due_date: string | null;
      days_overdue: number;
    };
  }>;
};

type StaffRow = {
  id: number;
  name: string;
  role: string | null;
  image: string | null;
  initials: string;
  weekly_class_count: number;
};

type DayTab = {
  date: string;
  weekday: string;
  weekday_short: string;
  total_classes: number;
  total_unique_students: number;
  is_today: boolean;
};

const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;
const parseUrlDate = (raw: string | null): Dayjs => {
  if (raw && isoDateRe.test(raw)) {
    const d = dayjs(raw);
    if (d.isValid()) return d;
  }
  return dayjs();
};

const TodayPage: React.FC = () => {
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  // Selected date is persisted in the URL as ?date=YYYY-MM-DD so refresh,
  // sharing, and browser Back/Forward all round-trip correctly. Absent or
  // malformed values fall back to the actual current date.
  const [selectedDate, setSelectedDate] = useState<Dayjs>(() =>
    parseUrlDate(searchParams.get("date")),
  );

  // Keep local state in sync with browser Back/Forward, which change the URL
  // without going through our setSelectedDate path.
  useEffect(() => {
    const raw = searchParams.get("date");
    const next = parseUrlDate(raw);
    if (!next.isSame(selectedDate, "day")) setSelectedDate(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Single update path — writes to the URL, which then reflects into state.
  const goToDate = useCallback(
    (next: Dayjs) => {
      const iso = next.format("YYYY-MM-DD");
      setSelectedDate(next);
      const params = new URLSearchParams(searchParams);
      if (params.get("date") !== iso) {
        params.set("date", iso);
        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const [filters, setFilters] = useState<TodayFilters>(EMPTY_FILTERS);

  // Drawer state — legacy fallback. Class-tile clicks now navigate
  // to the canonical per-occurrence page (see openOccurrenceDetails
  // below); this state is kept for the ClassDrawer's programmatic
  // callers but is no longer set from the schedule matrix.
  const [openOccurrenceId, setOpenOccurrenceId] = useState<number | null>(null);

  // Canonical navigation for a class-tile click. Points at the
  // per-occurrence CLASS DETAILS page — NOT the attendance report
  // and NOT the attendance-entry workspace. All three are distinct
  // user intents (Details / Entry / Report) and must never share
  // the same destination. `return_url` carries the selected date +
  // current query state so Back lands on the same day/filter scope.
  const navigate = useNavigate();
  const openOccurrenceDetails = useCallback(
    (occurrenceId: number) => {
      const dateParam = searchParams.toString();
      const returnUrl = `/today${dateParam ? `?${dateParam}` : ""}`;
      navigate(
        `/class-sessions/${occurrenceId}/details?return_url=${encodeURIComponent(returnUrl)}`,
      );
    },
    [navigate, searchParams],
  );

  // Only the server-facing filter fields belong in the query key — client
  // filters must NOT trigger a refetch (they hide already-loaded cards).
  const { data, isLoading, isFetching, isError, refetch } = useGetTimetableWeekQuery({
    reference_date: selectedDate.format("YYYY-MM-DD"),
    selected_date: selectedDate.format("YYYY-MM-DD"),
    location_id: filters.location_id || undefined,
    mode: filters.mode || undefined,
    staff_id: filters.staff_id || undefined,
  });

  const payload = (data as any)?.data;
  const days: DayTab[] = payload?.days ?? [];
  // Server summary is retained only as the loading skeleton fallback —
  // the display summary is derived below from the same array feeding
  // the schedule matrix so the two cannot drift under client filters
  // (search / spots / makeup / outstanding never reach the backend).
  const serverSummary: TodaySummary | undefined = payload?.summary;
  const canSeeFinance: boolean = !!payload?.can_see_finance;
  const allOccurrences: OccurrenceRow[] = payload?.occurrences ?? [];
  const staffRows: StaffRow[] = payload?.staff ?? [];

  // Apply client-side filters. The grid (Phase 4) reads from this memo,
  // so any card that shouldn't show simply never gets rendered — no
  // second data pass, no fetch. Search matches student names AND staff
  // names to satisfy "Search by student or staff name" in the spec.
  const filteredOccurrences = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const staffById = new Map(staffRows.map((s) => [s.id, s.name.toLowerCase()]));

    return allOccurrences.filter((o) => {
      if (filters.makeupOnly && !o.is_makeup) return false;
      if (filters.hasSpotsOnly && (o.is_cancelled || o.available_spots <= 0)) return false;
      if (filters.outstandingOnly && !o.students.some((s) => s.has_outstanding_invoice)) return false;
      if (term) {
        const staffName = (o.staff_id && staffById.get(o.staff_id)) || "";
        const hit =
          staffName.includes(term) ||
          o.students.some((s) => s.name.toLowerCase().includes(term));
        if (!hit) return false;
      }
      return true;
    });
  }, [allOccurrences, filters, staffRows]);

  const selectedIso = selectedDate.format("YYYY-MM-DD");

  // Client-derived KPI summary — same-day slice of the filtered array
  // that ScheduleMatrix will render. Any active filter (server-side
  // location/mode/staff_id AND client-side search/spots/makeup/
  // outstanding) is reflected here. When still fetching and the
  // filtered array is empty, fall back to the server summary so the
  // skeleton values don't flash 0.
  const filteredDayOccurrences = useMemo(
    () => filteredOccurrences.filter((o) => o.scheduled_date === selectedIso),
    [filteredOccurrences, selectedIso],
  );
  const derivedSummary = useMemo(
    () => deriveTodaySummary(filteredDayOccurrences, selectedIso),
    [filteredDayOccurrences, selectedIso],
  );
  const summary: TodaySummary | undefined =
    isFetching && filteredDayOccurrences.length === 0 ? serverSummary : derivedSummary;

  // Date-navigation gate — spec §1 / §6. RTK Query keeps the previously
  // fulfilled payload in `data` while a new request is in flight, so a
  // date change ordinarily flashes the OLD date's rows under the NEW
  // heading. Detect the transition by comparing the payload's echoed
  // `selected_date` against the currently selected ISO — when they
  // disagree AND we're fetching, the user is waiting for the new date's
  // snapshot to arrive. `isLoading` (first-fetch) is the same gate under
  // a different label, so the two flow through the same branch.
  const payloadDate: string | undefined = payload?.selected_date;
  const isDateNavigating = isFetching && !!payloadDate && payloadDate !== selectedIso;

  if (isLoading || isDateNavigating) {
    return <Loader />;
  }

  // Fetch failure — retryable state whenever the currently selected date
  // has no successful snapshot behind it. Covers both the initial-load
  // failure (no data at all) AND a failure while navigating to a new
  // date (spec §13: "Do not restore stale previous-date data as though
  // it were current"). Never renders alongside a spinner (spec §9).
  const noSnapshotForSelectedDate = !data || (!!payloadDate && payloadDate !== selectedIso);
  if (isError && noSnapshotForSelectedDate) {
    return (
      <Box
        role="alert"
        sx={{
          minHeight: "100vh", bgcolor: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 380, px: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "text.primary", mb: 0.75 }}>
            Couldn't load Today
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            The server didn't respond. Check your connection and try again.
          </Typography>
          <Button variant="contained" onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "10px" }}>
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      // position: relative anchors the branded content-overlay loader
      // that fires on filter changes (see below). Date navigation still
      // takes the full-page Loader branch above; this overlay only
      // covers same-date filter refetches so the user sees the same
      // logo loader instead of only the thin top progress bar.
      position: "relative",
      p: { xs: 2, md: 3 },
      // Premium page background — soft off-white gradient with a
      // whisper of blue, per redesign spec §2. Cards remain bright
      // and readable on top.
      background: "linear-gradient(180deg, #F6F9FC 0%, #F4F7FB 45%, #F8FAFC 100%)",
      minHeight: "calc(100vh - 64px)",
    }}>
      {/* ─── Command Header — premium navy → teal hero, replaces the
             flat title. Contains date title, scheduled counts, and the
             attached date-control group so it reads as one connected
             executive panel. Handlers/routes unchanged. ────────────── */}
      <Box
        sx={{
          mb: 2.5,
          borderRadius: "20px",
          overflow: "hidden",
          position: "relative",
          background: "linear-gradient(135deg, #10243E 0%, #1e3a5f 40%, #0C5A46 100%)",
          color: "#fff",
          boxShadow: "0 10px 30px rgba(16,36,62,0.25)",
        }}
      >
        {/* Decorative radial glows (pure CSS, no image assets). */}
        <Box sx={{
          position: "absolute", top: -60, right: -60, width: 260, height: 260,
          background: "radial-gradient(circle, rgba(34,166,153,0.35) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: -50, left: "40%", width: 220, height: 220,
          background: "radial-gradient(circle, rgba(79,110,247,0.22) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: { xs: 2.5, md: 3 }, position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: "14px",
                background: "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))",
                color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(4px)",
                "& svg": { fontSize: 26 },
              }}
            >
              <TodayOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2,
                textTransform: "uppercase", color: "rgba(255,255,255,0.72)",
                lineHeight: 1.1,
              }}>
                Today at Step Up
              </Typography>
              <Typography sx={{
                fontSize: { xs: 22, md: 26 }, fontWeight: 800,
                color: "#fff", lineHeight: 1.2, mt: 0.5,
                letterSpacing: -0.4,
              }}>
                {selectedDate.format("dddd, MMMM D, YYYY")}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                {isFetching && !summary ? (
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>Loading…</Typography>
                ) : (
                  <>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      <Box component="span" sx={{ color: "#7ee7d7", fontWeight: 800, mr: 0.5 }}>
                        {summary?.total_classes ?? 0}
                      </Box>
                      Class{(summary?.total_classes ?? 0) === 1 ? "" : "es"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      <Box component="span" sx={{ color: "#a5c9ff", fontWeight: 800, mr: 0.5 }}>
                        {summary?.total_unique_students ?? 0}
                      </Box>
                      Student{(summary?.total_unique_students ?? 0) === 1 ? "" : "s"} Expected
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>

          {/* Connected date-controls group — attached row with premium
              glassy chrome. Every underlying handler is preserved. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: "12px",
              p: 0.5,
              backdropFilter: "blur(6px)",
              flexWrap: { xs: "wrap", md: "nowrap" },
              "& .MuiIconButton-root": {
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
              },
            }}
          >
            <Tooltip title="Previous week" arrow>
              <IconButton
                size="small"
                onClick={() => goToDate(selectedDate.subtract(7, "day"))}
                aria-label="Previous week"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                <ChevronLeftIcon /><ChevronLeftIcon sx={{ ml: -1.5 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Previous day" arrow>
              <IconButton
                size="small"
                onClick={() => goToDate(selectedDate.subtract(1, "day"))}
                aria-label="Previous day"
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{
              width: { xs: 150, sm: 170 },
              // Force the inner date input to render on a light surface
              // so the hero's dark background doesn't fight readability.
              "& .MuiInputBase-root": {
                bgcolor: "rgba(255,255,255,0.95)",
                borderRadius: "8px",
                "& fieldset": { borderColor: "transparent" },
              },
            }}>
              <AppDatePicker
                value={selectedDate.format("YYYY-MM-DD")}
                onChange={(v) => {
                  if (v) goToDate(dayjs(v));
                }}
                ariaLabel="Select date"
                clearable={false}
                size="small"
                fullWidth
              />
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => goToDate(dayjs())}
              disabled={selectedDate.isSame(dayjs(), "day")}
              aria-label="Go to today"
              sx={{
                textTransform: "none", minWidth: 72, fontWeight: 700,
                bgcolor: selectedDate.isSame(dayjs(), "day") ? "rgba(255,255,255,0.2)" : "#22A699",
                color: "#fff",
                boxShadow: "none",
                "&:hover": { bgcolor: "#0C5A46" },
                "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" },
              }}
            >
              Today
            </Button>
            <Tooltip title="Next day" arrow>
              <IconButton
                size="small"
                onClick={() => goToDate(selectedDate.add(1, "day"))}
                aria-label="Next day"
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next week" arrow>
              <IconButton
                size="small"
                onClick={() => goToDate(selectedDate.add(7, "day"))}
                aria-label="Next week"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                <ChevronRightIcon /><ChevronRightIcon sx={{ ml: -1.5 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* ─── Week navigator: 7 day cards ─────────────────────────────
             Each day is a compact card with weekday/date + class &
             student counts. Selected day gets a deep teal border,
             mint fill, and stronger shadow; the current-day card
             wears a small TODAY badge. Hover lifts subtly. Handlers
             unchanged — same goToDate(dayjs(d.date)) as before. */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
          p: 1.25,
          mb: 2.5,
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 1,
          overflowX: "auto",
        }}
      >
        {isFetching && days.length === 0
          ? Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: "12px" }} />
            ))
          : days.map((d) => {
              const isSelected = d.date === selectedIso;
              const isToday = d.is_today;
              return (
                <Box
                  key={d.date}
                  role="button"
                  tabIndex={0}
                  onClick={() => goToDate(dayjs(d.date))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToDate(dayjs(d.date)); }
                  }}
                  sx={{
                    cursor: "pointer",
                    p: 1.5,
                    borderRadius: "12px",
                    border: isSelected
                      ? "2px solid #0C5A46"
                      : "2px solid transparent",
                    background: isSelected
                      ? "linear-gradient(135deg, #E8F7F1 0%, #d5eee0 100%)"
                      : isToday
                        ? "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)"
                        : "#F8FAFC",
                    boxShadow: isSelected
                      ? "0 4px 12px rgba(12,90,70,0.20)"
                      : "0 1px 2px rgba(15,23,42,0.04)",
                    transition: "transform 180ms ease-out, box-shadow 180ms ease-out",
                    display: "flex", flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isSelected
                        ? "0 8px 20px rgba(12,90,70,0.25)"
                        : "0 4px 10px rgba(15,23,42,0.08)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${isSelected ? "#0C5A46" : "#4F6EF7"}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{
                      fontSize: 10.5, fontWeight: 800,
                      color: isSelected ? "#0C5A46" : "text.secondary",
                      textTransform: "uppercase", letterSpacing: 0.6,
                    }}>
                      {d.weekday_short}
                    </Typography>
                    {isToday && (
                      <Chip
                        label="TODAY"
                        size="small"
                        sx={{
                          height: 16, fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5,
                          bgcolor: isSelected ? "#0C5A46" : "#F59E0B",
                          color: "#fff",
                        }}
                      />
                    )}
                  </Stack>
                  <Typography sx={{
                    fontSize: 16, fontWeight: 800,
                    color: isSelected ? "#0C5A46" : "#0f172a",
                    lineHeight: 1.15, mt: 0.5,
                  }}>
                    {dayjs(d.date).format("MMM D")}
                  </Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                    <Typography sx={{
                      fontSize: 10.5,
                      color: isSelected ? "#0C5A46" : "#475569",
                      fontWeight: 600,
                    }}>
                      <Box component="span" sx={{ fontWeight: 800 }}>{d.total_classes}</Box> Classes
                    </Typography>
                    <Typography sx={{
                      fontSize: 10.5,
                      color: isSelected ? "#0C5A46" : "#475569",
                      fontWeight: 600,
                    }}>
                      <Box component="span" sx={{ fontWeight: 800 }}>{d.total_unique_students}</Box> Students
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
      </Box>

      {/* ─── Summary cards ─────────────────────────────────────────── */}
      <SummaryCards summary={summary} loading={isFetching} canSeeFinance={canSeeFinance} />

      {/* ─── Filter toolbar shell ────────────────────────────────────
             Wraps the existing FilterBar in a premium tinted card
             with an eyebrow so it reads as a single connected surface
             instead of floating controls. FilterBar internals + every
             handler / onChange are unchanged. */}
      <Box
        sx={{
          mb: 2.5,
          borderRadius: "16px",
          bgcolor: "#fff",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
          overflow: "hidden",
        }}
      >
        <Box sx={{
          px: 2, pt: 1.25, pb: 0.75,
          borderBottom: "1px solid rgba(15,23,42,0.05)",
          background: "linear-gradient(180deg, #F8FAFC 0%, #ffffff 100%)",
        }}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: 1,
            textTransform: "uppercase", color: "#475569",
          }}>
            Find &amp; Filter
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <FilterBar filters={filters} onChange={setFilters} canSeeFinance={canSeeFinance} />
        </Box>
      </Box>

      {/* ─── Selected-day result count ─────────────────────────────── */}
      {(() => {
        const dayOccurrences = filteredDayOccurrences;
        const dayTotal = allOccurrences.filter((o) => o.scheduled_date === selectedIso).length;
        // "Truly empty day" vs "filters removed everything" — the two
        // need different empty-state copy. Any active filter (server
        // or client) with a filtered = 0 result means the user's
        // filters, not an empty schedule, is why the matrix is blank.
        const hasActiveFilter =
          !!filters.location_id ||
          !!filters.mode ||
          !!filters.staff_id ||
          !!filters.search.trim() ||
          filters.makeupOnly ||
          filters.hasSpotsOnly ||
          filters.outstandingOnly;
        const filtersRemovedEverything =
          !isFetching && hasActiveFilter && dayOccurrences.length === 0 && dayTotal > 0;
        return (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25, px: 0.5 }}>
              {isFetching ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>Loading…</Typography>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#10243E" }}>
                    {dayOccurrences.length}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>
                    of {dayTotal} classes on {selectedDate.format("MMM D")}
                    {dayOccurrences.length !== dayTotal && " (filtered)"}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {/* Staff-row × Class-column matrix (client-approved layout).
                DayList is kept in the tree for the mobile "day list" view if
                we want to reintroduce it later, but for the primary Today
                page we render the matrix everywhere so the layout matches
                the legacy timetable exactly. */}
            {filtersRemovedEverything ? (
              <Box
                sx={{
                  borderRadius: "16px",
                  border: "1px dashed rgba(15,23,42,0.15)",
                  bgcolor: "#fff",
                  p: { xs: 3, md: 5 },
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", mb: 0.5 }}>
                  No classes match your filters
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "#475569", mb: 2 }}>
                  {dayTotal} class{dayTotal === 1 ? "" : "es"} scheduled on {selectedDate.format("MMM D")} — none match the current filters.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Clear filters
                </Button>
              </Box>
            ) : (
              <ScheduleTimeline
                staffRows={staffRows}
                occurrences={dayOccurrences}
                selectedDate={selectedIso}
                onOpenClass={openOccurrenceDetails}
                canSeeFinance={canSeeFinance}
              />
            )}
          </>
        );
      })()}

      {/* ─── Legend ────────────────────────────────────────────────── */}
      <Legend />

      {/* ─── Class detail drawer ───────────────────────────────────── */}
      <ClassDrawer
        open={openOccurrenceId != null}
        occurrence={
          openOccurrenceId != null
            ? (allOccurrences.find((o) => o.id === openOccurrenceId) as ClassDrawerData | undefined) ?? null
            : null
        }
        staffName={
          openOccurrenceId != null
            ? staffRows.find(
                (s) => s.id === (allOccurrences.find((o) => o.id === openOccurrenceId)?.staff_id ?? null),
              )?.name
            : undefined
        }
        canSeeFinance={canSeeFinance}
        onClose={() => setOpenOccurrenceId(null)}
      />

      {/* Same-date filter refetches (location / mode / staff / search /
          spots / makeup / outstanding) get the branded content overlay,
          so filter changes show the Step Up logo loader — not only the
          top progress bar. Date navigation still runs through the
          full-page Loader branch at the top. */}
      <PageOverlayLoader visible={isFetching && !isLoading && !isDateNavigating} size="large" />
    </Box>
  );
};

export default TodayPage;
